import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle, Star, ExternalLink, Copy, MessageCircle, Mail, Printer, Send, ArrowLeft, ArrowRight, Play } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getVideoEmbed = (url) => {
  if (!url) return null;
  const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/);
  if (ytMatch) return `https://www.youtube-nocookie.com/embed/${ytMatch[1]}?modestbranding=1&rel=0`;
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  return null;
};

const toBullets = (text) => {
  if (!text) return [];
  return text.split(/(?<=[.!?])\s+/).map(s => s.trim().replace(/[.]+$/, '')).filter(s => s.length > 8);
};

// Country → flag emoji map. Falls back to text-only if not found.
const FLAG_BY_COUNTRY = {
  "Afghanistan":"🇦🇫","Albania":"🇦🇱","Algeria":"🇩🇿","Angola":"🇦🇴","Argentina":"🇦🇷","Armenia":"🇦🇲","Australia":"🇦🇺","Austria":"🇦🇹","Azerbaijan":"🇦🇿","Bahrain":"🇧🇭","Bangladesh":"🇧🇩","Belarus":"🇧🇾","Belgium":"🇧🇪","Benin":"🇧🇯","Bolivia":"🇧🇴","Bosnia and Herzegovina":"🇧🇦","Botswana":"🇧🇼","Brazil":"🇧🇷","Bulgaria":"🇧🇬","Burkina Faso":"🇧🇫","Burundi":"🇧🇮","Cambodia":"🇰🇭","Cameroon":"🇨🇲","Canada":"🇨🇦","Cape Verde":"🇨🇻","Chad":"🇹🇩","Chile":"🇨🇱","China":"🇨🇳","Colombia":"🇨🇴","Comoros":"🇰🇲","Congo":"🇨🇬","Congo (DRC)":"🇨🇩","Costa Rica":"🇨🇷","Côte d'Ivoire":"🇨🇮","Croatia":"🇭🇷","Cuba":"🇨🇺","Cyprus":"🇨🇾","Czech Republic":"🇨🇿","Denmark":"🇩🇰","Dominican Republic":"🇩🇴","Ecuador":"🇪🇨","Egypt":"🇪🇬","El Salvador":"🇸🇻","England":"🏴󠁧󠁢󠁥󠁮󠁧󠁿","Equatorial Guinea":"🇬🇶","Eritrea":"🇪🇷","Estonia":"🇪🇪","Eswatini":"🇸🇿","Ethiopia":"🇪🇹","Finland":"🇫🇮","France":"🇫🇷","Gabon":"🇬🇦","Gambia":"🇬🇲","Georgia":"🇬🇪","Germany":"🇩🇪","Ghana":"🇬🇭","Greece":"🇬🇷","Guatemala":"🇬🇹","Guinea":"🇬🇳","Haiti":"🇭🇹","Honduras":"🇭🇳","Hong Kong":"🇭🇰","Hungary":"🇭🇺","Iceland":"🇮🇸","India":"🇮🇳","Indonesia":"🇮🇩","Iran":"🇮🇷","Iraq":"🇮🇶","Ireland":"🇮🇪","Israel":"🇮🇱","Italy":"🇮🇹","Jamaica":"🇯🇲","Japan":"🇯🇵","Jordan":"🇯🇴","Kazakhstan":"🇰🇿","Kenya":"🇰🇪","Kosovo":"🇽🇰","Kuwait":"🇰🇼","Kyrgyzstan":"🇰🇬","Laos":"🇱🇦","Latvia":"🇱🇻","Lebanon":"🇱🇧","Lesotho":"🇱🇸","Liberia":"🇱🇷","Libya":"🇱🇾","Lithuania":"🇱🇹","Luxembourg":"🇱🇺","Madagascar":"🇲🇬","Malawi":"🇲🇼","Malaysia":"🇲🇾","Mali":"🇲🇱","Malta":"🇲🇹","Mauritania":"🇲🇷","Mauritius":"🇲🇺","Mexico":"🇲🇽","Moldova":"🇲🇩","Mongolia":"🇲🇳","Montenegro":"🇲🇪","Morocco":"🇲🇦","Mozambique":"🇲🇿","Myanmar":"🇲🇲","Namibia":"🇳🇦","Nepal":"🇳🇵","Netherlands":"🇳🇱","New Zealand":"🇳🇿","Nicaragua":"🇳🇮","Niger":"🇳🇪","Nigeria":"🇳🇬","North Korea":"🇰🇵","North Macedonia":"🇲🇰","Northern Ireland":"🏴󠁧󠁢󠁮󠁩󠁲󠁿","Norway":"🇳🇴","Oman":"🇴🇲","Pakistan":"🇵🇰","Palestine":"🇵🇸","Panama":"🇵🇦","Paraguay":"🇵🇾","Peru":"🇵🇪","Philippines":"🇵🇭","Poland":"🇵🇱","Portugal":"🇵🇹","Qatar":"🇶🇦","Romania":"🇷🇴","Russia":"🇷🇺","Rwanda":"🇷🇼","Saudi Arabia":"🇸🇦","Scotland":"🏴󠁧󠁢󠁳󠁣󠁴󠁿","Senegal":"🇸🇳","Serbia":"🇷🇸","Sierra Leone":"🇸🇱","Singapore":"🇸🇬","Slovakia":"🇸🇰","Slovenia":"🇸🇮","Somalia":"🇸🇴","South Africa":"🇿🇦","South Korea":"🇰🇷","South Sudan":"🇸🇸","Spain":"🇪🇸","Sri Lanka":"🇱🇰","Sudan":"🇸🇩","Sweden":"🇸🇪","Switzerland":"🇨🇭","Syria":"🇸🇾","Taiwan":"🇹🇼","Tanzania":"🇹🇿","Thailand":"🇹🇭","Togo":"🇹🇬","Trinidad and Tobago":"🇹🇹","Tunisia":"🇹🇳","Turkey":"🇹🇷","Turkmenistan":"🇹🇲","Uganda":"🇺🇬","Ukraine":"🇺🇦","United Arab Emirates":"🇦🇪","United Kingdom":"🇬🇧","United States":"🇺🇸","Uruguay":"🇺🇾","Uzbekistan":"🇺🇿","Venezuela":"🇻🇪","Vietnam":"🇻🇳","Wales":"🏴󠁧󠁢󠁷󠁬󠁳󠁿","Yemen":"🇾🇪","Zambia":"🇿🇲","Zimbabwe":"🇿🇼"
};
const flagOf = (country) => FLAG_BY_COUNTRY[country] || "";

// Strength category colors — quick visual identification for scouts
const CAT_PILL = {
  technical: "border-[#D4AF37]/45 text-[#E5C875] bg-[#D4AF37]/8",
  tactical: "border-[#6b8f6b]/55 text-[#9bbf9b] bg-[#6b8f6b]/8",
  physical: "border-[#C96A2E]/55 text-[#E89865] bg-[#C96A2E]/8",
  mental: "border-[#7088B5]/55 text-[#9CB0D6] bg-[#7088B5]/8",
};

const CAT_ICON = { technical: "⚡", tactical: "🧠", physical: "💪", mental: "🎯" };

export default function ProfileView({ profile, isPreview = false, strength = null }) {
  const [enquiry, setEnquiry] = useState({ scout_name: "", club: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const [showEnquiryForm, setShowEnquiryForm] = useState(false);

  const gen = profile.generated_profile || {};
  const edited = profile.edited_fields || {};
  const identity = profile.onboarding_data?.identity || {};
  const contact = profile.onboarding_data?.contact || {};
  const get = (key) => edited[key] || gen[key] || "";
  const profileImageUrl = profile.profile_image_path
    ? `${BACKEND_URL}/api/media/${profile.profile_image_path}`
    : "https://static.prod-images.emergentagent.com/jobs/1bc1e0bc-6716-4e3a-9c11-cd3b13fbaefa/images/1c30bf8bedb2266f7b8ce4ba5eca516cc891a4de6141fdb5b7fea0d0ccfddc2e.png";

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const shareMsg = `Hi, I'm a ${identity.primary_position || "player"} currently playing for ${identity.current_club || "my club"}. Here is my full player profile with footage: ${shareUrl}`;
  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(shareMsg)}`;
  const emailUrl = `mailto:?subject=${encodeURIComponent(`Player Profile - ${profile.name}`)}&body=${encodeURIComponent(shareMsg)}`;
  const copyLink = () => { navigator.clipboard.writeText(shareUrl); toast.success("Link copied to clipboard"); };

  const hasVideo = profile.highlight_video;
  const videoEmbed = getVideoEmbed(profile.highlight_video);

  const psText = get("playing_style");
  const psSentences = toBullets(psText);
  const psIntro = psSentences.length > 0 ? psSentences[0] + "." : "";
  const psBullets = psSentences.slice(1);

  const tacticalSections = gen.tactical_profile ? [
    { label: "IN POSSESSION", bullets: toBullets(gen.tactical_profile.in_possession) },
    { label: "OUT OF POSSESSION", bullets: toBullets(gen.tactical_profile.out_of_possession) },
    { label: "TRANSITIONS", bullets: toBullets(gen.tactical_profile.transitions) },
  ] : [];

  const expBullets = toBullets(get("experience_summary"));
  const devBullets = toBullets(get("development_focus"));
  const contactEmail = contact.contact_email || "";
  const flag = flagOf(identity.nationality);

  const handleEnquiry = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await (await import("../lib/api")).default.post("/enquiry", { ...enquiry, player_profile_id: profile.slug });
      toast.success("Enquiry submitted successfully");
      setEnquiry({ scout_name: "", club: "", email: "", message: "" });
      setShowEnquiryForm(false);
    } catch { toast.error("Failed to submit enquiry"); }
    setSending(false);
  };

  const scrollToContact = () => {
    const el = document.getElementById("contact-section");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    setShowEnquiryForm(true);
  };

  return (
    <div className={`bg-[#0A0A0A] min-h-screen ${!isPreview ? "pb-24" : ""}`} data-testid={isPreview ? "profile-preview" : "public-profile"}>
      {/* DRAFT BANNER */}
      {isPreview && (
        <div className="bg-[#D4AF37]/8 border-b border-[#D4AF37]/20 px-4 py-2.5" data-testid="preview-banner">
          <div className="max-w-[1060px] mx-auto flex items-center justify-between">
            <div>
              <span className="text-[10px] font-bold text-[#D4AF37] uppercase tracking-wider">DRAFT PREVIEW</span>
              <span className="text-[11px] text-[#888] ml-2">This is how your profile will appear when published</span>
            </div>
            {strength && (
              <span className="text-[11px] font-bold text-[#D4AF37]/80 tabular-nums" data-testid="preview-strength">Profile Strength: {strength.score}%</span>
            )}
          </div>
        </div>
      )}

      {/* SHARE BAR / BACK LINK */}
      {isPreview ? (
        <div className="max-w-[1060px] mx-auto px-6 pt-4">
          <Link to="/profile/edit" className="text-[#777] hover:text-[#F5F5F5] transition-colors flex items-center gap-1 text-xs" data-testid="back-to-editor">
            <ArrowLeft size={12} /> Back to Editor
          </Link>
        </div>
      ) : (
        <div className="bg-[#0E0E0E] border-b border-[#1A1A1A]">
          <div className="max-w-[1060px] mx-auto px-6 py-2.5 flex items-center justify-between">
            <span className="text-[11px] text-[#666] uppercase tracking-[0.18em] font-bold">Player Profile</span>
            <div className="flex items-center gap-1" data-testid="share-bar">
              <button onClick={() => window.print()} className="text-[#888] text-[11px] font-bold uppercase tracking-wider px-2 py-1 hover:text-[#D4AF37] transition-colors flex items-center gap-1" data-testid="share-print"><Printer size={13} />PDF</button>
              <span className="text-[#2A2A2A]">|</span>
              <button onClick={copyLink} className="text-[#888] text-[11px] font-bold uppercase tracking-wider px-2 py-1 hover:text-[#D4AF37] transition-colors flex items-center gap-1" data-testid="share-copy"><Copy size={13} />Copy</button>
              <span className="text-[#2A2A2A]">|</span>
              <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="text-[#888] text-[11px] font-bold uppercase tracking-wider px-2 py-1 hover:text-[#D4AF37] transition-colors flex items-center gap-1" data-testid="share-whatsapp"><MessageCircle size={13} />WhatsApp</a>
              <span className="text-[#2A2A2A]">|</span>
              <a href={emailUrl} className="text-[#888] text-[11px] font-bold uppercase tracking-wider px-2 py-1 hover:text-[#D4AF37] transition-colors flex items-center gap-1" data-testid="share-email"><Mail size={13} />Email</a>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-[1060px] mx-auto px-6">

        {/* HERO */}
        <section className="py-10 sm:py-12 border-b border-[#1A1A1A]" data-testid="profile-hero">
          <div className="grid grid-cols-1 md:grid-cols-[1fr_240px] gap-8 items-start">
            <div className="min-w-0">
              {(profile.is_reviewed || profile.is_featured) && (
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {profile.is_reviewed && <span className="text-[11px] font-bold uppercase tracking-[0.12em] text-[#D4AF37] border border-[#D4AF37]/25 bg-[#D4AF37]/5 px-2.5 py-1 flex items-center gap-1" data-testid="verified-badge"><CheckCircle size={11} />Verified Profile</span>}
                  {profile.is_featured && <span className="text-[11px] font-bold uppercase tracking-[0.12em] text-[#D4AF37] border border-[#D4AF37]/25 bg-[#D4AF37]/5 px-2.5 py-1 flex items-center gap-1"><Star size={11} />Featured</span>}
                </div>
              )}
              <h1 className="text-[2.6rem] sm:text-[3.2rem] font-black tracking-[-0.04em] uppercase text-[#F5F5F5] leading-[0.9] mb-3" data-testid="player-name">{profile.name}</h1>
              <p className="text-[17px] text-[#cccccc] leading-[1.6] mb-5" data-testid="player-headline">{get("headline")}</p>
              <div className="flex flex-wrap items-center gap-2">
                {identity.primary_position && (
                  <span className="text-[13px] font-semibold text-[#D4AF37] border border-[#D4AF37]/35 bg-[#D4AF37]/8 px-3 py-1.5 rounded-sm">{identity.primary_position}</span>
                )}
                {identity.age && (
                  <span className="text-[13px] font-semibold text-[#E5E5E5] border border-[#262626] px-3 py-1.5 rounded-sm">{identity.age} yrs</span>
                )}
                {identity.preferred_foot && (
                  <span className="text-[13px] font-semibold text-[#E5E5E5] border border-[#262626] px-3 py-1.5 rounded-sm">{identity.preferred_foot}-footed</span>
                )}
                {identity.nationality && (
                  <span className="text-[13px] font-semibold text-[#E5E5E5] border border-[#262626] px-3 py-1.5 rounded-sm flex items-center gap-1.5" data-testid="nationality-chip">
                    {flag && <span className="text-[15px] leading-none">{flag}</span>}
                    {identity.nationality}
                  </span>
                )}
                {identity.height && (
                  <span className="text-[13px] font-semibold text-[#E5E5E5] border border-[#262626] px-3 py-1.5 rounded-sm">{identity.height} cm</span>
                )}
              </div>
            </div>
            <div className="order-first md:order-last">
              <div className="aspect-[3/4] overflow-hidden border border-[#1A1A1A] bg-[#0E0E0E]">
                <img src={profileImageUrl} alt={profile.name} className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
        </section>

        {/* TOP CTA — preview only */}
        {isPreview && (
          <div className="py-5 border-b border-[#1A1A1A] flex items-center justify-between" data-testid="preview-top-cta">
            <span className="text-[13px] text-[#999]">Your profile is ready to go live</span>
            <Link to="/paywall" className="bg-[#D4AF37] text-black font-bold uppercase tracking-wider text-[11px] px-5 py-2.5 rounded-sm hover:bg-[#C5A059] transition-colors flex items-center gap-1.5" data-testid="preview-top-cta-btn">
              PUBLISH — $19 <ArrowRight size={12} />
            </Link>
          </div>
        )}

        {/* VIDEO — moved above the fold (first decision moment for scouts) */}
        {hasVideo ? (
          <section className="py-10 sm:py-12 border-b border-[#1A1A1A]" data-testid="section-video">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-[12px] font-bold text-[#D4AF37] uppercase tracking-[0.18em] flex items-center gap-2"><Play size={12} /> Highlight Reel</h2>
              <span className="text-[11px] text-[#666] uppercase tracking-wider">Watch first</span>
            </div>
            {videoEmbed ? (
              <div className="aspect-video bg-[#0E0E0E] border border-[#1A1A1A] overflow-hidden rounded-sm">
                <iframe src={videoEmbed} className="w-full h-full" allowFullScreen title="Highlights" />
              </div>
            ) : (
              <a href={profile.highlight_video} target="_blank" rel="noopener noreferrer" className="bg-[#0E0E0E] border border-[#1A1A1A] p-5 flex items-center gap-2.5 hover:border-[#D4AF37]/40 transition-colors rounded-sm">
                <ExternalLink size={18} className="text-[#D4AF37]" />
                <span className="text-[15px] text-[#ccc]">View Highlight Video</span>
              </a>
            )}
          </section>
        ) : isPreview ? (
          <section className="py-10 sm:py-12 border-b border-[#1A1A1A]" data-testid="section-video-empty">
            <h2 className="text-[12px] font-bold text-[#D4AF37] uppercase tracking-[0.18em] mb-3">Highlight Reel</h2>
            <div className="bg-[#0E0E0E] border border-dashed border-[#262626] p-6 text-center rounded-sm">
              <p className="text-[13px] text-[#888] mb-2">Add your highlight video — this is the first thing scouts look at</p>
              <Link to="/profile/edit" className="text-[11px] text-[#D4AF37] font-bold uppercase tracking-wider hover:underline">ADD VIDEO</Link>
            </div>
          </section>
        ) : null}

        {/* OVERVIEW */}
        {get("overview") && (
          <section className="py-10 sm:py-12 border-b border-[#1A1A1A]" data-testid="section-overview">
            <h2 className="text-[12px] font-bold text-[#D4AF37] uppercase tracking-[0.18em] mb-4">Player Overview</h2>
            <p className="text-[16px] text-[#dddddd] leading-[1.75]">{get("overview")}</p>
          </section>
        )}

        {/* KEY STRENGTHS — pill cloud, color-coded by category */}
        {gen.key_attributes && (
          <section className="py-10 sm:py-12 border-b border-[#1A1A1A]" data-testid="section-attributes">
            <h2 className="text-[12px] font-bold text-[#D4AF37] uppercase tracking-[0.18em] mb-4">Key Strengths</h2>
            <div className="flex flex-wrap gap-2" data-testid="strengths-cloud">
              {["technical", "tactical", "physical", "mental"].flatMap((cat) => {
                const attrs = gen.key_attributes[cat] || [];
                return attrs.map((a, i) => (
                  <span
                    key={`${cat}-${i}`}
                    className={`inline-flex items-center gap-1.5 text-[13px] font-medium border px-3 py-1.5 rounded-full ${CAT_PILL[cat] || ""}`}
                    title={cat.charAt(0).toUpperCase() + cat.slice(1)}
                    data-testid={`strength-${cat}-${i}`}
                  >
                    <span className="text-[11px] opacity-70">{CAT_ICON[cat]}</span>
                    {a}
                  </span>
                ));
              })}
            </div>
            <div className="flex flex-wrap gap-3 mt-5 text-[11px] text-[#666] uppercase tracking-wider">
              <span className="flex items-center gap-1.5"><span className="w-2 h-2 bg-[#D4AF37]/60 rounded-full" /> Technical</span>
              <span className="flex items-center gap-1.5"><span className="w-2 h-2 bg-[#9bbf9b]/60 rounded-full" /> Tactical</span>
              <span className="flex items-center gap-1.5"><span className="w-2 h-2 bg-[#E89865]/60 rounded-full" /> Physical</span>
              <span className="flex items-center gap-1.5"><span className="w-2 h-2 bg-[#9CB0D6]/60 rounded-full" /> Mental</span>
            </div>
          </section>
        )}

        {/* PLAYING STYLE */}
        {psText && (
          <section className="py-10 sm:py-12 border-b border-[#1A1A1A]" data-testid="section-playing-style">
            <h2 className="text-[12px] font-bold text-[#D4AF37] uppercase tracking-[0.18em] mb-4">Playing Style</h2>
            {psIntro && <p className="text-[16px] text-[#dddddd] leading-[1.75] mb-3">{psIntro}</p>}
            {psBullets.length > 0 && (
              <ul className="space-y-2">
                {psBullets.map((b, i) => (
                  <li key={i} className="text-[15px] text-[#cccccc] leading-[1.6] flex items-start gap-2.5">
                    <span className="text-[#D4AF37]/70 mt-[7px] flex-shrink-0 text-[8px]">&#9670;</span>{b}
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}

        {/* TACTICAL PROFILE — side-by-side on desktop for fast scanning */}
        {tacticalSections.length > 0 && (
          <section className="py-10 sm:py-12 border-b border-[#1A1A1A]" data-testid="section-tactical">
            <h2 className="text-[12px] font-bold text-[#D4AF37] uppercase tracking-[0.18em] mb-5">Key Match Actions</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {tacticalSections.map((sec, i) => sec.bullets.length > 0 && (
                <div key={i} className="bg-[#0E0E0E] border border-[#1A1A1A] p-4 rounded-sm">
                  <div className="text-[11px] font-bold text-[#D4AF37] uppercase tracking-[0.15em] mb-3 pb-2 border-b border-[#1A1A1A]">{sec.label}</div>
                  <ul className="space-y-2">
                    {sec.bullets.map((b, j) => (
                      <li key={j} className="text-[14px] text-[#cccccc] leading-[1.55] flex items-start gap-2">
                        <span className="text-[#D4AF37]/70 mt-[6px] flex-shrink-0 text-[7px]">&#9670;</span>{b}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* EXPERIENCE */}
        {expBullets.length > 0 && (
          <section className="py-10 sm:py-12 border-b border-[#1A1A1A]" data-testid="section-experience">
            <h2 className="text-[12px] font-bold text-[#D4AF37] uppercase tracking-[0.18em] mb-4">Experience</h2>
            <ul className="space-y-2">
              {expBullets.map((b, i) => (
                <li key={i} className="text-[15px] text-[#dddddd] leading-[1.65] flex items-start gap-2.5">
                  <span className="text-[#D4AF37]/70 mt-[7px] flex-shrink-0 text-[8px]">&#9670;</span>{b}
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* DEVELOPMENT FOCUS */}
        {devBullets.length > 0 && (
          <section className="py-10 sm:py-12 border-b border-[#1A1A1A]" data-testid="section-development">
            <h2 className="text-[12px] font-bold text-[#D4AF37] uppercase tracking-[0.18em] mb-4">Development Profile</h2>
            <ul className="space-y-2">
              {devBullets.map((b, i) => (
                <li key={i} className="text-[15px] text-[#cccccc] leading-[1.65] flex items-start gap-2.5">
                  <span className="text-[#D4AF37]/70 mt-[7px] flex-shrink-0 text-[8px]">&#9670;</span>{b}
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* PLAYER DETAILS */}
        <section className="py-10 sm:py-12 border-b border-[#1A1A1A]" data-testid="section-details">
          <h2 className="text-[12px] font-bold text-[#D4AF37] uppercase tracking-[0.18em] mb-5">Player Details</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-4">
            {[
              { label: "Full Name", value: identity.full_name || profile.name },
              identity.age && { label: "Age", value: identity.age },
              identity.nationality && { label: "Nationality", value: `${flag ? flag + " " : ""}${identity.nationality}` },
              identity.height && { label: "Height", value: `${identity.height} cm` },
              identity.primary_position && { label: "Primary Position", value: identity.primary_position },
              identity.secondary_position && identity.secondary_position !== "None" && { label: "Secondary Position", value: identity.secondary_position },
              identity.preferred_foot && { label: "Preferred Foot", value: identity.preferred_foot },
              identity.current_club && { label: "Current Club", value: identity.current_club },
              (contact.availability && contact.availability !== "Not Currently Available" && contact.availability !== "Not Currently")
                ? { label: "Availability", value: contact.availability, highlight: true }
                : null,
            ].filter(Boolean).map((item, i) => (
              <div key={i} className="flex flex-col">
                <span className="text-[11px] font-bold text-[#666] uppercase tracking-[0.12em] mb-1">{item.label}</span>
                <span className={`text-[15px] font-medium ${item.highlight ? "text-green-400" : "text-[#eeeeee]"}`}>{item.value}</span>
              </div>
            ))}
          </div>
        </section>

        {/* CONTACT / ENQUIRY (live) or BOTTOM CTA (preview) */}
        {isPreview ? (
          <section className="py-10 text-center" data-testid="preview-bottom-cta">
            <p className="text-[15px] text-[#A3A3A3] mb-5">You're one step away from making this live</p>
            <Link to="/paywall" className="inline-flex items-center gap-2 bg-[#D4AF37] text-black font-bold uppercase tracking-wider text-[13px] px-7 py-3.5 rounded-full hover:bg-[#E5C04A] transition-all" data-testid="preview-bottom-cta-btn">
              PUBLISH — $19 <ArrowRight size={14} />
            </Link>
            <div className="mt-4">
              <Link to="/profile/edit" className="text-[11px] text-[#666] hover:text-[#999] transition-colors" data-testid="preview-edit-btn">or continue editing</Link>
            </div>
          </section>
        ) : (
          <section id="contact-section" className="py-10" data-testid="section-enquiry">
            <h2 className="text-[12px] font-bold text-[#D4AF37] uppercase tracking-[0.18em] mb-4">Contact This Player</h2>
            <p className="text-[15px] text-[#aaaaaa] leading-[1.65] mb-6">
              For further information, additional clips, player background or discussion regarding suitable opportunities — make contact below.
            </p>
            <div className="flex flex-wrap items-center gap-3 mb-5">
              {contactEmail && (
                <a href={`mailto:${contactEmail}`} className="bg-[#D4AF37] text-black font-bold uppercase tracking-wider text-[13px] px-6 py-3 rounded-sm hover:bg-[#E5C04A] transition-colors inline-flex items-center gap-2" data-testid="contact-email-btn">
                  <Mail size={15} /> EMAIL DIRECTLY
                </a>
              )}
              <button onClick={() => setShowEnquiryForm(!showEnquiryForm)} className="bg-transparent border border-[#333] text-[#eeeeee] font-bold uppercase tracking-wider text-[13px] px-6 py-3 rounded-sm hover:border-[#D4AF37]/60 transition-colors inline-flex items-center gap-2" data-testid="enquiry-toggle">
                <Send size={15} /> SEND ENQUIRY
              </button>
            </div>
            {showEnquiryForm && (
              <form onSubmit={handleEnquiry} className="bg-[#0E0E0E] border border-[#1A1A1A] p-6 space-y-3 animate-fade-in rounded-sm" data-testid="enquiry-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <input type="text" value={enquiry.scout_name} onChange={(e) => setEnquiry((p) => ({ ...p, scout_name: e.target.value }))} className="input-dark text-[14px] py-3" placeholder="Your name" required data-testid="enquiry-name" />
                  <input type="text" value={enquiry.club} onChange={(e) => setEnquiry((p) => ({ ...p, club: e.target.value }))} className="input-dark text-[14px] py-3" placeholder="Club / Organisation" required data-testid="enquiry-club" />
                </div>
                <input type="email" value={enquiry.email} onChange={(e) => setEnquiry((p) => ({ ...p, email: e.target.value }))} className="input-dark text-[14px] py-3" placeholder="Email" required data-testid="enquiry-email" />
                <textarea value={enquiry.message} onChange={(e) => setEnquiry((p) => ({ ...p, message: e.target.value }))} className="input-dark text-[14px] py-3 min-h-[100px] resize-y" placeholder="Message" required data-testid="enquiry-message" />
                <button type="submit" disabled={sending} className="bg-[#D4AF37] text-black font-bold uppercase tracking-wider text-[13px] px-8 py-3 rounded-sm hover:bg-[#E5C04A] transition-colors inline-flex items-center gap-2 disabled:opacity-50" data-testid="enquiry-submit">
                  {sending ? <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" /> : <Send size={14} />}
                  {sending ? "SENDING..." : "SUBMIT ENQUIRY"}
                </button>
              </form>
            )}
          </section>
        )}
      </div>

      {/* STICKY BOTTOM CTA — live profiles only */}
      {!isPreview && (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-md border-t border-[#1A1A1A] py-3 print:hidden" data-testid="sticky-contact-bar">
          <div className="max-w-[1060px] mx-auto px-4 sm:px-6 flex items-center justify-between gap-3">
            <div className="hidden sm:block min-w-0 flex-1">
              <p className="text-[13px] font-bold text-[#F5F5F5] truncate">Interested in {profile.name}?</p>
              <p className="text-[11px] text-[#888]">Reach out below — direct line to the player.</p>
            </div>
            <div className="flex items-center gap-2 flex-1 sm:flex-initial">
              {contactEmail ? (
                <a href={`mailto:${contactEmail}?subject=${encodeURIComponent("Enquiry about " + profile.name)}`} className="flex-1 sm:flex-initial inline-flex items-center justify-center gap-1.5 bg-[#D4AF37] text-black font-bold uppercase tracking-wider text-[11px] sm:text-[12px] px-4 sm:px-5 py-2.5 rounded-full hover:bg-[#E5C04A] transition-all" data-testid="sticky-email">
                  <Mail size={13} /> Email
                </a>
              ) : null}
              <button onClick={scrollToContact} className="flex-1 sm:flex-initial inline-flex items-center justify-center gap-1.5 bg-transparent border border-[#D4AF37]/40 text-[#D4AF37] font-bold uppercase tracking-wider text-[11px] sm:text-[12px] px-4 sm:px-5 py-2.5 rounded-full hover:bg-[#D4AF37]/10 transition-colors" data-testid="sticky-enquiry">
                <Send size={13} /> Send Enquiry
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
